// ============================================
// AUDIO MANAGER
// ============================================
// Manages all game audio (SFX and background music)

class AudioManager {
  constructor() {
    this.ctx = null;
    this.masterGain = null;
    this.initialized = false;

    // Background Music
    this.bgm = null;
    this.isMusicPlaying = false;
    this.fadeInterval = null;
    this.musicInitialized = false;
    this.currentSourceIndex = 0;
    this.musicLoadAttempts = 0;
    this.currentMusicSources = [];
    this.musicLoadPromise = null;
  }

  init() {
    if (this.initialized) return;

    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      this.ctx = new AudioContext();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = 0.3;
      this.masterGain.connect(this.ctx.destination);
      this.initialized = true;
      console.log('✅ Audio context initialized');
    } catch (e) {
      console.error('❌ Audio init failed', e);
    }
  }

  resumeContext() {
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  }

  // --- SFX Methods (Synthesized) ---
  playTone(freq, type, duration, vol = 1) {
    if (!this.initialized) this.init();
    if (!this.ctx) return;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(freq, this.ctx.currentTime);
    gain.gain.setValueAtTime(vol, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.ctx.currentTime + duration);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start();
    osc.stop(this.ctx.currentTime + duration);
  }

  playStep() {
    this.playTone(100 + Math.random() * 50, 'square', 0.05, 0.1);
  }

  playCollect() {
    this.playTone(880, 'square', 0.1, 0.3);
    setTimeout(() => this.playTone(1760, 'square', 0.2, 0.3), 100);
  }

  playSpawnDoor() {
    this.playTone(110, 'sawtooth', 1.0, 0.4);
    setTimeout(() => this.playTone(220, 'sine', 1.0, 0.4), 200);
  }

  playDoorEnter() {
    this.playTone(440, 'square', 0.1, 0.3);
    setTimeout(() => this.playTone(660, 'square', 0.1, 0.3), 100);
    setTimeout(() => this.playTone(880, 'square', 0.4, 0.3), 200);
  }

  playDamage() {
    this.playTone(100, 'sawtooth', 0.3, 0.5);
    this.playTone(50, 'square', 0.3, 0.5);
  }

  playAbility() {
    this.playTone(440, 'sine', 0.5, 0.4);
    setTimeout(() => this.playTone(880, 'sine', 0.5, 0.4), 100);
    setTimeout(() => this.playTone(220, 'square', 0.8, 0.2), 200);
  }

  // --- Background Music Methods ---
  
  // Load music with specific sources
  loadMusic(musicSources) {
    // If already playing the same sources, don't reload
    if (this.isMusicPlaying && 
        JSON.stringify(this.currentMusicSources) === JSON.stringify(musicSources)) {
      console.log('🎵 Music already playing with same sources');
      return Promise.resolve();
    }

    // Stop current music if playing different sources
    if (this.isMusicPlaying) {
      this.stopMusic(0);
    }

    this.currentMusicSources = musicSources;
    this.currentSourceIndex = 0;
    this.musicLoadAttempts = 0;
    this.musicInitialized = false;

    return this.tryNextMusicSource();
  }

  tryNextMusicSource() {
    if (this.currentSourceIndex >= this.currentMusicSources.length) {
      console.error('❌ All music sources failed. Music will not play.');
      console.info('💡 To enable music: Download the MP3 file and place it in public/audio/');
      return Promise.reject('All music sources failed');
    }

    const source = this.currentMusicSources[this.currentSourceIndex];
    console.log(`🎵 Trying music source ${this.currentSourceIndex + 1}/${this.currentMusicSources.length}:`, source);

    return new Promise((resolve, reject) => {
      if (!this.bgm) {
        this.bgm = new Audio();
        this.bgm.loop = true;
        this.bgm.volume = 0.4;
        this.bgm.preload = "auto";
      }

      // Remove old listeners
      this.bgm.oncanplaythrough = null;
      this.bgm.onerror = null;

      // Set up event listeners
      this.bgm.oncanplaythrough = () => {
        console.log('✅ Music loaded successfully from source:', source);
        this.musicLoadAttempts = 0;
        this.musicInitialized = true;
        resolve();
      };

      this.bgm.onerror = (e) => {
        console.warn(`⚠️ Music source ${this.currentSourceIndex + 1} failed:`, e);
        this.currentSourceIndex++;
        this.musicLoadAttempts++;

        if (this.musicLoadAttempts < this.currentMusicSources.length) {
          setTimeout(() => {
            this.tryNextMusicSource().then(resolve).catch(reject);
          }, 500);
        } else {
          reject('All sources failed');
        }
      };

      // Set source and load
      this.bgm.src = source;
      this.bgm.load();
    });
  }

  playMusic(musicSources = null) {
    // If sources provided, load them first
    if (musicSources) {
      this.musicLoadPromise = this.loadMusic(musicSources);
    }

    // If music is already playing, don't restart
    if (this.bgm && !this.bgm.paused) {
      console.log('🎵 Music already playing');
      return;
    }

    // Initialize music if needed
    if (!this.musicInitialized && !musicSources) {
      console.warn('⚠️ No music sources provided and music not initialized');
      return;
    }

    // Cancel any existing fade out
    if (this.fadeInterval) {
      clearInterval(this.fadeInterval);
      this.fadeInterval = null;
      if (this.bgm) {
        this.bgm.volume = 0.4;
      }
    }

    // Wait for music to load if needed, then play
    const attemptPlay = () => {
      if (this.bgm) {
        const playPromise = this.bgm.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('✅ Music playback started successfully');
              this.isMusicPlaying = true;
            })
            .catch(e => {
              console.error('❌ Music playback failed:', e);
              this.isMusicPlaying = false;

              if (e.name === 'NotAllowedError') {
                console.warn('⚠️ Music autoplay blocked by browser. User interaction required.');
              } else if (e.name === 'NotSupportedError') {
                this.currentSourceIndex++;
                if (this.currentSourceIndex < this.currentMusicSources.length) {
                  console.log('🔄 Trying next music source...');
                  this.musicInitialized = false;
                  setTimeout(() => this.playMusic(), 500);
                }
              }
            });
        }
      }
    };

    if (this.musicLoadPromise) {
      this.musicLoadPromise.then(attemptPlay).catch(console.error);
    } else {
      attemptPlay();
    }
  }

  stopMusic(fadeDuration = 0) {
    if (!this.bgm) return;

    console.log('⏹️ Stopping music with fade duration:', fadeDuration);

    // Clear any existing fade
    if (this.fadeInterval) {
      clearInterval(this.fadeInterval);
      this.fadeInterval = null;
    }

    if (fadeDuration > 0 && !this.bgm.paused) {
      const originalVolume = 0.4;
      const steps = 20;
      const stepTime = fadeDuration / steps;
      const volStep = originalVolume / steps;
      let currentStep = 0;

      this.fadeInterval = setInterval(() => {
        currentStep++;
        const newVol = Math.max(0, originalVolume - (volStep * currentStep));
        if (this.bgm) {
          this.bgm.volume = newVol;
        }

        if (currentStep >= steps) {
          if (this.fadeInterval) {
            clearInterval(this.fadeInterval);
            this.fadeInterval = null;
          }
          if (this.bgm) {
            this.bgm.pause();
            this.bgm.currentTime = 0;
            this.bgm.volume = 0.4;
          }
          this.isMusicPlaying = false;
        }
      }, stepTime);
    } else {
      this.bgm.pause();
      this.bgm.currentTime = 0;
      this.bgm.volume = 0.4;
      this.isMusicPlaying = false;
    }
  }
}

export const audioManager = new AudioManager();